import { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Settings,
  CreditCard,
  Shield,
  Mail,
  Upload,
  Globe,
  Database,
  Server,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  Save,
  TestTube,
  Lock,
  Unlock,
  RefreshCw,
  Calculator,
  Palette
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';
import adminService from '@/services/adminService';
import systemSettingsService, { SystemSettings, SystemSettingsUpdate } from '@/services/systemSettingsService';
import DatabaseDashboard from '@/components/admin/DatabaseDashboard';
import EstampadosManager from '@/components/admin/EstampadosManager';

interface PaymentSettings {
  provider: string;
  isActive: boolean;
  paguelofacil: {
    apiKey: string;
    cclw: string;
    sandboxApiKey: string;
    sandboxCclw: string;
    useSandbox: boolean;
  };
  currentEnvironment: {
    mode: 'sandbox' | 'production';
    urls: {
      baseUrl: string;
      apiUrl: string;
    };
  };
  updatedAt: string;
}

interface SystemInfo {
  nodeVersion: string;
  environment: string;
  databaseStatus: 'connected' | 'disconnected';
  uptime: string;
  memoryUsage: {
    used: number;
    total: number;
  };
}

const PaymentSettingsCard = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showCredentials, setShowCredentials] = useState({
    apiKey: false,
    cclw: false,
    sandboxApiKey: false,
    sandboxCclw: false
  });
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<PaymentSettings | null>(null);

  // Query para obtener configuración de pagos
  const { data: paymentSettings, isLoading } = useQuery({
    queryKey: ['admin-payment-settings'],
    queryFn: () => adminService.getPaymentSettings(),
  });

  // Mutación para actualizar configuración
  const updateMutation = useMutation({
    mutationFn: (settings: Partial<PaymentSettings['paguelofacil']>) => 
      adminService.updatePaymentSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-payment-settings'] });
      setIsEditing(false);
      setFormData(null);
      toast({
        title: "Configuración actualizada",
        description: "La configuración de pagos se ha actualizado exitosamente",
      });
    },
    onError: (error: unknown) => {
      let errorMessage = "Error al actualizar la configuración";
      if (error && typeof error === 'object' && 'response' in error && 
          error.response && typeof error.response === 'object' && 'data' in error.response &&
          error.response.data && typeof error.response.data === 'object' && 'message' in error.response.data) {
        errorMessage = String(error.response.data.message);
      }
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  });

  // Mutación para probar conexión
  const testMutation = useMutation({
    mutationFn: () => adminService.testPaymentConnection(),
    onSuccess: (data) => {
      toast({
        title: data.success ? "Conexión exitosa" : "Error de conexión",
        description: data.message,
        variant: data.success ? "default" : "destructive"
      });
    },
    onError: (error: unknown) => {
      let errorMessage = "Error al probar la conexión";
      if (error && typeof error === 'object' && 'response' in error && 
          error.response && typeof error.response === 'object' && 'data' in error.response &&
          error.response.data && typeof error.response.data === 'object' && 'message' in error.response.data) {
        errorMessage = String(error.response.data.message);
      }
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  });

  const handleEdit = () => {
    setFormData(paymentSettings);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setFormData(null);
    setIsEditing(false);
  };

  const handleSave = () => {
    if (formData) {
      updateMutation.mutate(formData.paguelofacil);
    }
  };

  const handleTest = () => {
    testMutation.mutate();
  };

  const toggleCredentialVisibility = (field: keyof typeof showCredentials) => {
    setShowCredentials(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const maskCredential = (value: string, visible: boolean) => {
    if (!value) return '';
    return visible ? value : `${'*'.repeat(Math.max(8, value.length - 4))}${value.slice(-4)}`;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="space-y-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (!paymentSettings) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Configuración de Pagos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h3 className="font-medium text-lg mb-2">No hay configuración de pagos</h3>
            <p className="text-muted-foreground mb-4">
              Configure su gateway de pagos para comenzar a procesar pedidos
            </p>
            <Button onClick={handleEdit}>
              Configurar Pagos
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const currentData = formData || paymentSettings;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Configuración de Pagos - PagueloFacil
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant={paymentSettings.isActive ? 'default' : 'secondary'}>
              {paymentSettings.isActive ? 'Activo' : 'Inactivo'}
            </Badge>
            <Badge variant={paymentSettings.currentEnvironment.mode === 'production' ? 'destructive' : 'secondary'}>
              {paymentSettings.currentEnvironment.mode === 'production' ? 'Producción' : 'Sandbox'}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Estado del Ambiente */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-500 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900">Ambiente Actual</h4>
              <p className="text-sm text-blue-800 mt-1">
                Modo: <strong>{paymentSettings.currentEnvironment.mode}</strong>
              </p>
              <p className="text-sm text-blue-800">
                URL Base: <strong>{paymentSettings.currentEnvironment.urls.baseUrl}</strong>
              </p>
              <p className="text-sm text-blue-800">
                API URL: <strong>{paymentSettings.currentEnvironment.urls.apiUrl}</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Configuración del Modo */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium">Modo Sandbox</h4>
              <p className="text-sm text-muted-foreground">
                Activar para usar el ambiente de pruebas
              </p>
            </div>
            <Switch
              checked={isEditing ? formData?.paguelofacil.useSandbox : paymentSettings.paguelofacil.useSandbox}
              onCheckedChange={(checked) => {
                if (isEditing && formData) {
                  setFormData({
                    ...formData,
                    paguelofacil: {
                      ...formData.paguelofacil,
                      useSandbox: checked
                    }
                  });
                }
              }}
              disabled={!isEditing}
            />
          </div>
        </div>

        <Separator />

        {/* Credenciales de Producción */}
        <div className="space-y-4">
          <h4 className="font-medium flex items-center gap-2">
            <Lock className="h-4 w-4" />
            Credenciales de Producción
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">API Key</label>
              <div className="flex gap-2">
                <Input
                  type={showCredentials.apiKey ? 'text' : 'password'}
                  value={isEditing ? (formData?.paguelofacil.apiKey || '') : maskCredential(paymentSettings.paguelofacil.apiKey, showCredentials.apiKey)}
                  onChange={(e) => {
                    if (isEditing && formData) {
                      setFormData({
                        ...formData,
                        paguelofacil: {
                          ...formData.paguelofacil,
                          apiKey: e.target.value
                        }
                      });
                    }
                  }}
                  disabled={!isEditing}
                  placeholder="API Key de producción"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleCredentialVisibility('apiKey')}
                >
                  {showCredentials.apiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">CCLW</label>
              <div className="flex gap-2">
                <Input
                  type={showCredentials.cclw ? 'text' : 'password'}
                  value={isEditing ? (formData?.paguelofacil.cclw || '') : maskCredential(paymentSettings.paguelofacil.cclw, showCredentials.cclw)}
                  onChange={(e) => {
                    if (isEditing && formData) {
                      setFormData({
                        ...formData,
                        paguelofacil: {
                          ...formData.paguelofacil,
                          cclw: e.target.value
                        }
                      });
                    }
                  }}
                  disabled={!isEditing}
                  placeholder="Código CCLW de producción"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleCredentialVisibility('cclw')}
                >
                  {showCredentials.cclw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Credenciales de Sandbox */}
        <div className="space-y-4">
          <h4 className="font-medium flex items-center gap-2">
            <TestTube className="h-4 w-4" />
            Credenciales de Sandbox (Pruebas)
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Sandbox API Key</label>
              <div className="flex gap-2">
                <Input
                  type={showCredentials.sandboxApiKey ? 'text' : 'password'}
                  value={isEditing ? (formData?.paguelofacil.sandboxApiKey || '') : maskCredential(paymentSettings.paguelofacil.sandboxApiKey, showCredentials.sandboxApiKey)}
                  onChange={(e) => {
                    if (isEditing && formData) {
                      setFormData({
                        ...formData,
                        paguelofacil: {
                          ...formData.paguelofacil,
                          sandboxApiKey: e.target.value
                        }
                      });
                    }
                  }}
                  disabled={!isEditing}
                  placeholder="API Key de sandbox"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleCredentialVisibility('sandboxApiKey')}
                >
                  {showCredentials.sandboxApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Sandbox CCLW</label>
              <div className="flex gap-2">
                <Input
                  type={showCredentials.sandboxCclw ? 'text' : 'password'}
                  value={isEditing ? (formData?.paguelofacil.sandboxCclw || '') : maskCredential(paymentSettings.paguelofacil.sandboxCclw, showCredentials.sandboxCclw)}
                  onChange={(e) => {
                    if (isEditing && formData) {
                      setFormData({
                        ...formData,
                        paguelofacil: {
                          ...formData.paguelofacil,
                          sandboxCclw: e.target.value
                        }
                      });
                    }
                  }}
                  disabled={!isEditing}
                  placeholder="Código CCLW de sandbox"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleCredentialVisibility('sandboxCclw')}
                >
                  {showCredentials.sandboxCclw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Acciones */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleTest}
            disabled={testMutation.isPending}
            className="flex items-center gap-2"
          >
            <TestTube className="h-4 w-4" />
            {testMutation.isPending ? 'Probando...' : 'Probar Conexión'}
          </Button>

          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <Button variant="outline" onClick={handleCancel}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                  className="flex items-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  {updateMutation.isPending ? 'Guardando...' : 'Guardar'}
                </Button>
              </>
            ) : (
              <Button onClick={handleEdit} className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Editar
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const GeneralSettingsCard = () => {
  const [settings, setSettings] = useState({
    siteName: 'Vivi con Amor y Arte',
    siteDescription: 'Tu tienda online de productos únicos',
    contactEmail: 'contacto@viviconamoryarte.com',
    supportEmail: 'soporte@viviconamoryarte.com',
    phone: '+507 6000-0000',
    address: 'Ciudad de Panamá, Panamá',
    currency: 'USD',
    language: 'es',
    timezone: 'America/Panama'
  });

  const [isEditing, setIsEditing] = useState(false);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="h-5 w-5" />
          Configuración General
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Nombre del Sitio</label>
            <Input
              value={settings.siteName}
              onChange={(e) => setSettings(prev => ({ ...prev, siteName: e.target.value }))}
              disabled={!isEditing}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Email de Contacto</label>
            <Input
              type="email"
              value={settings.contactEmail}
              onChange={(e) => setSettings(prev => ({ ...prev, contactEmail: e.target.value }))}
              disabled={!isEditing}
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-2">Descripción del Sitio</label>
            <Input
              value={settings.siteDescription}
              onChange={(e) => setSettings(prev => ({ ...prev, siteDescription: e.target.value }))}
              disabled={!isEditing}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Teléfono</label>
            <Input
              value={settings.phone}
              onChange={(e) => setSettings(prev => ({ ...prev, phone: e.target.value }))}
              disabled={!isEditing}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Moneda</label>
            <Select
              value={settings.currency}
              onValueChange={(value) => setSettings(prev => ({ ...prev, currency: value }))}
              disabled={!isEditing}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD - Dólar Americano</SelectItem>
                <SelectItem value="PAB">PAB - Balboa Panameño</SelectItem>
                <SelectItem value="EUR">EUR - Euro</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-2">Dirección</label>
            <Input
              value={settings.address}
              onChange={(e) => setSettings(prev => ({ ...prev, address: e.target.value }))}
              disabled={!isEditing}
            />
          </div>
        </div>

        <div className="flex items-center justify-end pt-4 border-t">
          {isEditing ? (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Cancelar
              </Button>
              <Button onClick={() => setIsEditing(false)} className="flex items-center gap-2">
                <Save className="h-4 w-4" />
                Guardar
              </Button>
            </div>
          ) : (
            <Button onClick={() => setIsEditing(true)} className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Editar
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const TaxSettingsCard = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<SystemSettings | null>(null);

  // Query para obtener configuración del sistema
  const { data: systemSettings, isLoading } = useQuery({
    queryKey: ['admin-system-settings'],
    queryFn: () => systemSettingsService.getSystemSettings(),
  });

  // Mutación para actualizar configuración
  const updateMutation = useMutation({
    mutationFn: (updates: SystemSettingsUpdate) => 
      systemSettingsService.updateSystemSettings(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-system-settings'] });
      setIsEditing(false);
      setFormData(null);
      toast({
        title: "Configuración actualizada",
        description: "La configuración de impuestos se ha actualizado exitosamente",
      });
    },
    onError: (error: unknown) => {
      let errorMessage = "Error al actualizar la configuración";
      if (error && typeof error === 'object' && 'response' in error && 
          error.response && typeof error.response === 'object' && 'data' in error.response &&
          error.response.data && typeof error.response.data === 'object' && 'message' in error.response.data) {
        errorMessage = String(error.response.data.message);
      }
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  });

  const handleEdit = useCallback(() => {
    if (systemSettings) {
      // Deep clone to avoid reference issues
      setFormData(JSON.parse(JSON.stringify(systemSettings)));
    }
    setIsEditing(true);
  }, [systemSettings]);

  const handleCancel = useCallback(() => {
    setFormData(null);
    setIsEditing(false);
  }, []);

  const handleSave = useCallback(() => {
    if (formData) {
      updateMutation.mutate({
        taxSettings: formData.taxSettings
      });
    }
  }, [formData, updateMutation]);

  // Memoized event handlers
  const handleTaxEnabledChange = useCallback((checked: boolean) => {
    if (isEditing && formData) {
      setFormData({
        ...formData,
        taxSettings: {
          ...formData.taxSettings,
          enabled: checked
        }
      });
    }
  }, [isEditing, formData]);

  const handleTaxNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (isEditing && formData) {
      setFormData({
        ...formData,
        taxSettings: {
          ...formData.taxSettings,
          name: e.target.value
        }
      });
    }
  }, [isEditing, formData]);

  const handleTaxRateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (isEditing && formData) {
      const percentage = parseFloat(e.target.value) || 0;
      setFormData({
        ...formData,
        taxSettings: {
          ...formData.taxSettings,
          rate: percentage / 100
        }
      });
    }
  }, [isEditing, formData]);

  const handleTaxDescriptionChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (isEditing && formData) {
      setFormData({
        ...formData,
        taxSettings: {
          ...formData.taxSettings,
          description: e.target.value
        }
      });
    }
  }, [isEditing, formData]);

  // Memoized computed values - must be before early returns
  const currentData = useMemo(() => formData || systemSettings, [formData, systemSettings]);

  const statusCardClassName = useMemo(() => 
    `${currentData?.taxSettings?.enabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'} border rounded-lg p-4`,
    [currentData?.taxSettings?.enabled]
  );

  const formattedTaxRate = useMemo(() => 
    currentData?.taxSettings ? systemSettingsService.formatTaxRate(currentData.taxSettings.rate) : '',
    [currentData?.taxSettings]
  );

  const calculatedTax = useMemo(() => 
    currentData?.taxSettings ? systemSettingsService.calculateTax(100, currentData.taxSettings) : 0,
    [currentData?.taxSettings]
  );

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (!systemSettings) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Configuración de Impuestos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h3 className="font-medium text-lg mb-2">No hay configuración de impuestos</h3>
            <p className="text-muted-foreground mb-4">
              Configure los impuestos del sistema
            </p>
            <Button onClick={handleEdit}>
              Configurar Impuestos
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Configuración de Impuestos
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant={currentData?.taxSettings?.enabled ? 'default' : 'secondary'}>
              {currentData?.taxSettings?.enabled ? 'Activo' : 'Desactivado'}
            </Badge>
            {currentData?.taxSettings?.enabled && (
              <Badge variant="outline">
                {formattedTaxRate}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Estado actual de impuestos */}
        <div className={statusCardClassName}>
          <div className="flex items-start gap-3">
            <Info className={`h-5 w-5 mt-0.5 ${currentData?.taxSettings?.enabled ? 'text-green-500' : 'text-gray-500'}`} />
            <div>
              <h4 className={`font-medium ${currentData?.taxSettings?.enabled ? 'text-green-900' : 'text-gray-900'}`}>
                Estado de Impuestos
              </h4>
              <p className={`text-sm mt-1 ${currentData?.taxSettings?.enabled ? 'text-green-800' : 'text-gray-600'}`}>
                {currentData?.taxSettings?.enabled 
                  ? `Los impuestos están activos al ${formattedTaxRate} (${currentData.taxSettings.name})`
                  : 'Los impuestos están desactivados - no se cobrará impuesto en las órdenes'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Configuración de impuestos */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium">Activar Impuestos</h4>
              <p className="text-sm text-muted-foreground">
                Habilitar o deshabilitar el cobro de impuestos en todas las órdenes
              </p>
            </div>
            <Switch
              checked={currentData?.taxSettings?.enabled || false}
              onCheckedChange={handleTaxEnabledChange}
              disabled={!isEditing}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Nombre del Impuesto</label>
              <Input
                value={currentData?.taxSettings?.name || ''}
                onChange={handleTaxNameChange}
                disabled={!isEditing}
                placeholder="Ej: ITBMS, IVA, Tax"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Porcentaje de Impuesto</label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={currentData?.taxSettings ? (currentData.taxSettings.rate * 100).toFixed(2) : '0.00'}
                  onChange={handleTaxRateChange}
                  disabled={!isEditing || !currentData?.taxSettings?.enabled}
                  placeholder="15.00"
                />
                <div className="flex items-center px-3 bg-gray-100 border rounded-md">
                  <span className="text-sm text-gray-600">%</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Ingrese el porcentaje (ej: 15 para 15%)
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Descripción</label>
            <Input
              value={currentData?.taxSettings?.description || ''}
              onChange={handleTaxDescriptionChange}
              disabled={!isEditing}
              placeholder="Descripción del impuesto para mostrar a los clientes"
            />
          </div>
        </div>

        {/* Vista previa de cálculo */}
        {currentData?.taxSettings?.enabled && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Vista Previa de Cálculo</h4>
            <div className="space-y-1 text-sm text-blue-800">
              <div className="flex justify-between">
                <span>Subtotal de ejemplo:</span>
                <span>$100.00</span>
              </div>
              <div className="flex justify-between">
                <span>{currentData.taxSettings.name} ({formattedTaxRate}):</span>
                <span>${calculatedTax.toFixed(2)}</span>
              </div>
              <Separator className="my-2" />
              <div className="flex justify-between font-medium">
                <span>Total:</span>
                <span>${(100 + calculatedTax).toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Acciones */}
        <div className="flex items-center justify-end pt-4 border-t">
          {isEditing ? (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleCancel}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSave}
                disabled={updateMutation.isPending}
                className="flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {updateMutation.isPending ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
          ) : (
            <Button onClick={handleEdit} className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Editar
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const SecuritySettingsCard = () => {
  const [settings, setSettings] = useState({
    requireStrongPasswords: true,
    sessionTimeout: 30,
    maxLoginAttempts: 5,
    twoFactorAuth: false,
    ipWhitelist: false,
    auditLogging: true
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Configuración de Seguridad
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium">Contraseñas Seguras</h4>
              <p className="text-sm text-muted-foreground">
                Requerir contraseñas con al menos 8 caracteres, mayúsculas, minúsculas y números
              </p>
            </div>
            <Switch
              checked={settings.requireStrongPasswords}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, requireStrongPasswords: checked }))}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium">Autenticación de Dos Factores</h4>
              <p className="text-sm text-muted-foreground">
                Requerir verificación adicional para administradores
              </p>
            </div>
            <Switch
              checked={settings.twoFactorAuth}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, twoFactorAuth: checked }))}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium">Registro de Auditoría</h4>
              <p className="text-sm text-muted-foreground">
                Mantener un registro de todas las acciones administrativas
              </p>
            </div>
            <Switch
              checked={settings.auditLogging}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, auditLogging: checked }))}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Tiempo de Sesión (minutos)</label>
              <Input
                type="number"
                value={settings.sessionTimeout}
                onChange={(e) => setSettings(prev => ({ ...prev, sessionTimeout: parseInt(e.target.value) }))}
                min="5"
                max="480"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Máximo Intentos de Login</label>
              <Input
                type="number"
                value={settings.maxLoginAttempts}
                onChange={(e) => setSettings(prev => ({ ...prev, maxLoginAttempts: parseInt(e.target.value) }))}
                min="3"
                max="10"
              />
            </div>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-900">Configuración Avanzada</h4>
              <p className="text-sm text-amber-800 mt-1">
                Estos cambios de seguridad afectarán a todos los usuarios del sistema. Asegúrese de informar a los usuarios antes de aplicar cambios.
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const SystemInfoCard = () => {
  const systemInfo: SystemInfo = {
    nodeVersion: 'v18.17.0',
    environment: 'development',
    databaseStatus: 'connected',
    uptime: '2h 15m',
    memoryUsage: {
      used: 156,
      total: 512
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Server className="h-5 w-5" />
          Información del Sistema
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Versión Node.js:</span>
              <span className="font-mono">{systemInfo.nodeVersion}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Ambiente:</span>
              <Badge variant={systemInfo.environment === 'production' ? 'destructive' : 'secondary'}>
                {systemInfo.environment}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Tiempo Activo:</span>
              <span>{systemInfo.uptime}</span>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Base de Datos:</span>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${systemInfo.databaseStatus === 'connected' ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className={systemInfo.databaseStatus === 'connected' ? 'text-green-600' : 'text-red-600'}>
                  {systemInfo.databaseStatus === 'connected' ? 'Conectada' : 'Desconectada'}
                </span>
              </div>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Memoria:</span>
              <span>{systemInfo.memoryUsage.used}MB / {systemInfo.memoryUsage.total}MB</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(systemInfo.memoryUsage.used / systemInfo.memoryUsage.total) * 100}%` }}
              />
            </div>
          </div>
        </div>

        <div className="pt-4 border-t">
          <Button variant="outline" className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Actualizar Información
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminSettings = () => {
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Configuración del Sistema</h1>
        <p className="text-muted-foreground">
          Administra la configuración general, pagos, seguridad e información del sistema
        </p>
      </div>

      {/* Tabs de Configuración */}
      <Tabs defaultValue="general" className="w-full">
        <TabsList className="w-full inline-flex overflow-x-auto gap-1 p-1 scrollbar-hide">
          <TabsTrigger value="general" className="flex items-center gap-2 whitespace-nowrap min-w-fit">
            <Globe className="h-4 w-4" />
            <span className="hidden sm:inline">General</span>
          </TabsTrigger>
          <TabsTrigger value="payments" className="flex items-center gap-2 whitespace-nowrap min-w-fit">
            <CreditCard className="h-4 w-4" />
            <span className="hidden sm:inline">Pagos</span>
          </TabsTrigger>
          <TabsTrigger value="taxes" className="flex items-center gap-2 whitespace-nowrap min-w-fit">
            <Calculator className="h-4 w-4" />
            <span className="hidden sm:inline">Impuestos</span>
          </TabsTrigger>
          <TabsTrigger value="estampados" className="flex items-center gap-2 whitespace-nowrap min-w-fit">
            <Palette className="h-4 w-4" />
            <span className="hidden sm:inline">Estampados</span>
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2 whitespace-nowrap min-w-fit">
            <Shield className="h-4 w-4" />
            <span className="hidden sm:inline">Seguridad</span>
          </TabsTrigger>
          <TabsTrigger value="system" className="flex items-center gap-2 whitespace-nowrap min-w-fit">
            <Server className="h-4 w-4" />
            <span className="hidden sm:inline">Sistema</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-6">
          <GeneralSettingsCard />
        </TabsContent>

        <TabsContent value="payments" className="space-y-6">
          <PaymentSettingsCard />
        </TabsContent>

        <TabsContent value="taxes" className="space-y-6">
          <TaxSettingsCard />
        </TabsContent>

        <TabsContent value="estampados" className="space-y-6">
          <EstampadosManager />
        </TabsContent>

        <TabsContent value="security" className="space-y-6">
          <SecuritySettingsCard />
        </TabsContent>

        <TabsContent value="system" className="space-y-6">
          <SystemInfoCard />
          <DatabaseDashboard />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminSettings;